import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { AxiosHttpClient } from "../../../services/AxiosHttpService";

export interface UserSettings {
  // Préférences générales
  language: string;
  timezone: string;
  theme: string;
  dateFormat: string;

  // Paramètres du chatbot
  chatbotLanguage: string;
  responseDetailLevel: string;
  showSources: string;
  explainConfidence: string;
  conversationStorageDuration: string;
  allowTraining: string;

  // Alertes
  alertsEnabled: string;
  alertEmail: string;
  alertInApp: string;
  alertFrequency: string;

  // Facturation
  companyName: string;
  address: string;
  vatNumber: string;
}

type SettingsContextType = {
  settings: UserSettings | null;
  loading: boolean;
  error: string | null;
  refreshSettings: () => void;
  updateSettings: (changedSettings: Record<string, string>) => Promise<void>;
};

const defaultSettings: UserSettings = {
  language: "fr",
  timezone: "europe/paris",
  theme: "system",
  dateFormat: "DD/MM/YYYY",
  chatbotLanguage: "fr",
  responseDetailLevel: "standard",
  showSources: "true",
  explainConfidence: "false",
  conversationStorageDuration: "30days",
  allowTraining: "false",
  alertsEnabled: "true",
  alertEmail: "true",
  alertInApp: "true",
  alertFrequency: "daily",
  companyName: "",
  address: "",
  vatNumber: "",
};

const SettingsContext = createContext<SettingsContextType>({
  settings: null,
  loading: true,
  error: null,
  refreshSettings: () => {},
  updateSettings: async () => {},
});

export const useSettings = () => useContext(SettingsContext);

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider = ({ children }: SettingsProviderProps) => {
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getSettings = useCallback(() => {
    setLoading(true);
    setError(null);

    AxiosHttpClient.get<
      Array<{ userId: string; parameterName: string; parameterValue: string }>
    >("api/v1/docuveritas/parameters")
      .then((res) => {
        // Transformer le tableau en objet clé-valeur
        const parametersMap: Record<string, string> = {};
        res.forEach((param) => {
          parametersMap[param.parameterName] = param.parameterValue;
        });

        // Fusionner les paramètres reçus avec les valeurs par défaut
        const mergedSettings = { ...defaultSettings, ...parametersMap };
        setSettings(mergedSettings);
      })
      .catch((error) => {
        console.error("Erreur lors de la récupération des paramètres:", error);
        setError("Impossible de charger les paramètres");
        // En cas d'erreur, utiliser les valeurs par défaut
        setSettings(defaultSettings);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const updateSettings = useCallback(
    async (changedSettings: Record<string, string>): Promise<void> => {
      try {
        // Créer le DTO avec le champ parameters comme attendu par le backend
        const requestDto = {
          parameters: changedSettings,
        };

        await AxiosHttpClient.put("api/v1/docuveritas/parameters", requestDto);

        // Mettre à jour les paramètres localement après succès
        if (settings) {
          const updatedSettings = { ...settings, ...changedSettings };
          setSettings(updatedSettings);
        }

        setError(null);
      } catch (error) {
        setError("Impossible de sauvegarder les paramètres");
        throw error; // Re-lancer l'erreur pour que le composant puisse la gérer
      }
    },
    [settings]
  );

  useEffect(() => {
    getSettings();
  }, [getSettings]);

  return (
    <SettingsContext.Provider
      value={{
        settings,
        loading,
        error,
        refreshSettings: getSettings,
        updateSettings,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};
